import { useMemo, useState } from 'react';
import { adminApi, type TemplateAdmin, type TemplateFieldAdmin } from '../../api/admin';
import { useAsync, Banner, Item, Empty } from './ui';
import { Modal } from '../../components/Modal';
import { isPmsTitle } from '../../lib/pmsGroup';

const emptyField = (sectionKey = 'section1', sectionLabel = 'Section 1'): TemplateFieldAdmin => ({
  sectionKey, sectionLabel, fieldKey: '', label: '', unit: '', dataType: 'decimal',
  required: true, sanityMin: null, sanityMax: null,
});

const emptyTemplate = (): TemplateAdmin => ({
  templateId: '', title: '', version: '1', fields: [emptyField()],
});

export default function TemplatesAdmin() {
  const { data, error, loading, reload } = useAsync(() => adminApi.listTemplates(), [], 'templates');
  const [form, setForm] = useState<TemplateAdmin | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<'daily' | 'pms'>('daily');

  const dailyTemplates = (data?.templates ?? []).filter((t) => !isPmsTitle(t.title));
  const pmsTemplates = (data?.templates ?? []).filter((t) => isPmsTitle(t.title));
  const shownTemplates = view === 'daily' ? dailyTemplates : pmsTemplates;

  function edit(t: TemplateAdmin) {
    setForm(JSON.parse(JSON.stringify(t)));
    setIsNew(false);
    setMsg('');
  }
  function create() {
    setForm(emptyTemplate());
    setIsNew(true);
    setMsg('');
  }

  async function save() {
    if (!form) return;
    setSaving(true); setMsg('');
    try {
      await adminApi.saveTemplate(form);
      setForm(null); reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  }

  async function del(t: TemplateAdmin) {
    if (!confirm(`Delete template "${t.title}"? This cannot be undone.`)) return;
    try { await adminApi.deleteTemplate(t.templateId); reload(); }
    catch (e) { setMsg(e instanceof Error ? e.message : 'Delete failed'); }
  }

  // ---- field editing (operates on the flat form.fields array) ----
  function patchField(i: number, patch: Partial<TemplateFieldAdmin>) {
    if (!form) return;
    const fields = form.fields.slice();
    fields[i] = { ...fields[i], ...patch };
    setForm({ ...form, fields });
  }
  function removeField(i: number) {
    if (!form || form.fields.length <= 1) return;
    setForm({ ...form, fields: form.fields.filter((_, idx) => idx !== i) });
  }
  /** swap two fields that belong to the same section (positions within that section) */
  function moveInSection(idxs: number[], pos: number, dir: -1 | 1) {
    if (!form) return;
    const b = pos + dir;
    if (b < 0 || b >= idxs.length) return;
    const fields = form.fields.slice();
    [fields[idxs[pos]], fields[idxs[b]]] = [fields[idxs[b]], fields[idxs[pos]]];
    setForm({ ...form, fields });
  }
  function renameSection(oldKey: string, patch: { sectionKey?: string; sectionLabel?: string }) {
    if (!form) return;
    setForm({
      ...form,
      fields: form.fields.map((f) => (f.sectionKey === oldKey ? { ...f, ...patch } : f)),
    });
  }
  function addFieldTo(sectionKey: string, sectionLabel: string, afterIdx: number) {
    if (!form) return;
    const fields = form.fields.slice();
    fields.splice(afterIdx + 1, 0, emptyField(sectionKey, sectionLabel));
    setForm({ ...form, fields });
  }
  function addSection() {
    if (!form) return;
    const n = groups.length + 1;
    setForm({ ...form, fields: [...form.fields, emptyField(`section${n}`, `Section ${n}`)] });
  }
  const optionsText = (f: TemplateFieldAdmin) => (f.options ?? []).join(', ');
  function setOptionsText(i: number, text: string) {
    patchField(i, { options: text.split(',').map((s) => s.trim()).filter(Boolean) });
  }

  const groups = useMemo(() => {
    const order: string[] = [];
    const byKey: Record<string, number[]> = {};
    (form?.fields ?? []).forEach((f, i) => {
      const k = f.sectionKey || '';
      if (!(k in byKey)) { byKey[k] = []; order.push(k); }
      byKey[k].push(i);
    });
    return order.map((k) => ({ key: k, label: form!.fields[byKey[k][0]].sectionLabel, idxs: byKey[k] }));
  }, [form]);

  return (
    <div>
      <div className="spread">
        <h1>Form templates</h1>
        <button className="sm" onClick={create}>+ New</button>
      </div>
      <p className="muted">
        A template defines the reading form for a type of equipment. Changing it changes the form
        for every equipment that uses it — new measurement columns are added to the sheet automatically.
      </p>
      <Banner error={error || msg} />
      {loading && <div className="spinner" />}

      <div className="choice-group" role="tablist" aria-label="Template type" style={{ marginBottom: 12 }}>
        <button type="button" className={`choice-btn ${view === 'daily' ? 'on' : ''}`} role="tab" aria-selected={view === 'daily'} onClick={() => setView('daily')}>
          Daily readings ({dailyTemplates.length})
        </button>
        <button type="button" className={`choice-btn ${view === 'pms' ? 'on' : ''}`} role="tab" aria-selected={view === 'pms'} onClick={() => setView('pms')}>
          PMS checks ({pmsTemplates.length})
        </button>
      </div>
      {data && shownTemplates.length === 0 && <Empty icon="📋">{`No ${view === 'daily' ? 'daily-reading' : 'PMS'} templates yet.`}</Empty>}
      <div className="cardlist">
        {shownTemplates.map((t) => (
          <Item
            key={t.templateId}
            title={<>{t.title} {t.builtin && <span className="chip" style={{ fontSize: '.7rem' }}>built-in</span>}</>}
            meta={<>
              <span>{t.templateId}</span>
              <span>v{t.version}</span>
              <span>{t.fields.length} fields · {sectionCount(t)} sections</span>
              <span>{t.usedBy?.length ?? 0} equipment</span>
            </>}
            actions={<>
              <button className="secondary xs" onClick={() => edit(t)}>Edit</button>
              {!t.builtin && (t.usedBy?.length ?? 0) === 0 && (
                <button className="secondary xs" onClick={() => del(t)}>Delete</button>
              )}
            </>}
          />
        ))}
      </div>

      {form && (
        <Modal
          wide
          title={isNew ? 'New template' : `Edit ${form.templateId}`}
          onClose={() => !saving && setForm(null)}
          footer={<>
            <button className="secondary" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
            <button onClick={save} disabled={saving || !form.templateId || !form.title}>
              {saving ? 'Saving…' : 'Save template'}
            </button>
          </>}
        >
          <div className="tf-top">
            <div className="col-wide">
              <label>Template ID</label>
              <input value={form.templateId} disabled={!isNew}
                onChange={(e) => setForm({ ...form, templateId: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
                placeholder="DG_READINGS" />
            </div>
            <div className="col-wide">
              <label>Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="DG Set Reading" />
            </div>
            <div>
              <label>Version</label>
              <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
            </div>
          </div>

          <h2>Sections &amp; fields</h2>
          <p className="muted tiny" style={{ marginTop: -4 }}>
            Fields in the same section are grouped under one heading on the reading form.
          </p>

          {groups.map((g) => (
            <div className="tf-section" key={g.key}>
              <div className="tf-section-head tf-mini">
                <div>
                  <label>Section name</label>
                  <input value={g.label}
                    onChange={(e) => renameSection(g.key, { sectionLabel: e.target.value })}
                    placeholder="Incomer VCB" />
                </div>
                <div>
                  <label>Section key</label>
                  <input value={g.key}
                    onChange={(e) => renameSection(g.key, { sectionKey: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                    placeholder="incomer" />
                </div>
              </div>

              {g.idxs.map((fi, pos) => {
                const f = form.fields[fi];
                return (
                  <div className="tf-card tf-mini" key={fi}>
                    <div className="tf-grid">
                      <div className="col-2">
                        <label>Label</label>
                        <input value={f.label} onChange={(e) => patchField(fi, { label: e.target.value })} placeholder="Voltage" />
                      </div>
                      <div className="col-2">
                        <label>Field key</label>
                        <input value={f.fieldKey}
                          onChange={(e) => patchField(fi, { fieldKey: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                          placeholder="dg_voltage" />
                      </div>
                      <div>
                        <label>Type</label>
                        <select value={f.dataType} onChange={(e) => patchField(fi, { dataType: e.target.value as TemplateFieldAdmin['dataType'] })}>
                          <option value="decimal">decimal</option>
                          <option value="int">integer</option>
                          <option value="text">text</option>
                          <option value="select">choice (Yes/No, …)</option>
                          <option value="photo">photo</option>
                          <option value="video">video</option>
                        </select>
                      </div>

                      {(f.dataType === 'decimal' || f.dataType === 'int') && (
                        <div>
                          <label>Unit</label>
                          <input value={f.unit} onChange={(e) => patchField(fi, { unit: e.target.value })} placeholder="V" />
                        </div>
                      )}

                      {f.dataType === 'select' && (
                        <div className="col-2">
                          <label>Options (comma-separated, at least 2)</label>
                          <input value={optionsText(f)} onChange={(e) => setOptionsText(fi, e.target.value)}
                            placeholder="Working, Not Working" />
                        </div>
                      )}

                      {(f.dataType === 'photo' || f.dataType === 'video') && (
                        <div className="col-2">
                          <p className="muted tiny" style={{ margin: 0 }}>
                            Captured with the phone's camera and uploaded automatically when the operator fills the form.
                          </p>
                        </div>
                      )}

                      {(f.dataType === 'decimal' || f.dataType === 'int') && (
                        <>
                          <div>
                            <label>Sane min</label>
                            <input inputMode="decimal" value={f.sanityMin ?? ''}
                              onChange={(e) => patchField(fi, { sanityMin: e.target.value === '' ? null : Number(e.target.value) })} />
                          </div>
                          <div>
                            <label>Sane max</label>
                            <input inputMode="decimal" value={f.sanityMax ?? ''}
                              onChange={(e) => patchField(fi, { sanityMax: e.target.value === '' ? null : Number(e.target.value) })} />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="tf-ctrls">
                      <label className="tf-req">
                        <input type="checkbox" checked={f.required}
                          onChange={(e) => patchField(fi, { required: e.target.checked })} /> Required
                      </label>
                      <span style={{ flex: 1 }} />
                      <button className="secondary" onClick={() => moveInSection(g.idxs, pos, -1)} disabled={pos === 0} aria-label="Move up">↑</button>
                      <button className="secondary" onClick={() => moveInSection(g.idxs, pos, 1)} disabled={pos === g.idxs.length - 1} aria-label="Move down">↓</button>
                      <button className="secondary" onClick={() => removeField(fi)} aria-label="Remove field">✕</button>
                    </div>
                  </div>
                );
              })}

              <button className="secondary sm" style={{ marginTop: 8 }}
                onClick={() => addFieldTo(g.key, g.label, g.idxs[g.idxs.length - 1])}>
                + Field in “{g.label}”
              </button>
            </div>
          ))}

          <button className="secondary" style={{ marginTop: 6 }} onClick={addSection}>+ Add section</button>
        </Modal>
      )}
    </div>
  );
}

function sectionCount(t: TemplateAdmin): number {
  return new Set(t.fields.map((f) => f.sectionKey)).size;
}
